<template>
  <div class="class-list-page">
    <list-page-header />

    <yv-index
      :path="path"
      :formSearch="formSearch"
      :columns="columns"
      :tableActionsComponent="TableActions"
      :sortable="sortable"
      :sort-data="sortData"
      :formatTableData="formatTableData"
      mainField="class_name"
      ref="classTableIns"
    >
    </yv-index>

    <common-modal
      :name="confirmModalProps.name"
      :title="confirmModalProps.title"
      :primaryAction="confirmModalProps.primaryAction"
      :secondaryActions="confirmModalProps.secondaryActions"
      height="auto"
    >
      <template #modal-body>
        {{ confirmModalProps.content }}
      </template>
    </common-modal>

    <common-modal
      :name="copyClassModalProps.name"
      :title="copyClassModalProps.title"
      :primaryAction="copyClassModalProps.primaryAction"
      :secondaryActions="copyClassModalProps.secondaryActions"
      height="auto"
    >
      <template #modal-body>
        <p class="desc mb-8">{{ copyClassModalProps.content.desc }}</p>
        <md-field>
          <label>Class name</label>
          <md-input
            v-model.trim="copyClassModalProps.content.className"
          ></md-input>
        </md-field>
        <div class="options mt-2">
          <md-checkbox
            v-model="copyClassModalProps.content.includeStudents"
            class="mb-0"
            >Include students when copying</md-checkbox
          >
          <md-checkbox
            v-model="copyClassModalProps.content.includeTeachers"
            class="mb-0"
            >Include teachers when copying</md-checkbox
          >
        </div>
      </template>
    </common-modal>
  </div>
</template>

<script>
import { YvIndex } from "@/components/Base";
import { Message, getId } from "@/helpers/utils";
import { service_paths, cms_service_paths  } from "@/constants/Services";
import Header from "./Header";
import TableActions from "./TableActions.vue";
import { TEACHER_ASSIGNMENTS } from "@/constants";
import { mapState, mapGetters } from "vuex";
import Role from "@/constants/Role";
import { hasValidRole, isDynamicRole } from "@/helpers/role-helper";
import { DEFAULT_OPTION_ALL } from "@/constants";
import { convertDateFromServer } from "@/helpers/datetime-helper";
import { status as statusOpts } from "@/pages/Classes/data";
import { generateStatus } from "@/helpers/text-helper";

export default {
  name: "ListPage",
  components: { YvIndex, ListPageHeader: Header },
  data() {
    const statusOptions = [...DEFAULT_OPTION_ALL, ...statusOpts];

    return {
      formSearch: [
        {
          label: "class.filter_by_name",
          name: "name__~",
          value: "",
          type: "input",
          width: 25
        },
        {
          label: "class.filter_by_term",
          name: "term_id",
          value: "",
          type: "select",
          is_switch_locale: "false",
          options: [],
          width: 25
        },
        {
          label: "class.filter_by_status",
          name: "status",
          value: "",
          type: "select",
          options: statusOptions,
          width: 25
        },
        {
          label: "common.source",
          name: "lms_id",
          value: "",
          type: "select",
          is_switch_locale: "false",
          options: [],
          width: 25
        },
        {
          label: "class.filter_by_teacher",
          name: "teacher_id",
          value: "",
          displayValue: "",
          options: [],
          type: "autocomplete",
          fields: {
            text: "name",
            value: "id"
          },
          searchKey: "username__~",
          refetchOptions: this.getTeacherOpts,
          width: 25
        },
        {
          label: "common.created_date",
          name: "created_date",
          value: [],
          type: "date-range",
          width: 35
        }
      ],
      columns: {
        name: "class.col_class_name",
        teacher: "class.col_teacher",
        term: "class.col_term",
        credit: "class.col_credit",
        status_class: "class.col_status",
        start_date: "class.col_start_date",
        end_date: "class.col_end_date",
        source: "common.source",
        external_id: "External id"
      },
      sortable: ["name", "credit", "status", "start_date", "end_date"],
      sortData: {
        currentSort: "id",
        currentSortOrder: "desc"
      },
      TableActions,
      confirmModalProps: {
        name: "confirm-modal",
        title: "common.confirmation",
        primaryAction: {
          title: "common.delete",
          actions: () => {},
          class: "md-danger"
        },
        secondaryActions: [
          {
            title: "common.cancel",
            action: () => {}
          }
        ],
        content: ""
      },
      copyClassModalProps: {
        name: "copy-class-modal",
        title: "",
        primaryAction: {
          title: "common.copy",
          action: () => {}
        },
        secondaryActions: [
          {
            title: "common.cancel",
            action: () => {
              this.$modal.hide("copy-class-modal");
            }
          }
        ],
        content: {
          desc: "",
          className: "",
          includeStudents: false,
          includeTeachers: false
        }
      }
    };
  },
  computed: {
    ...mapGetters("user", ["role"]),
    ...mapState("user", ["info"]),
    ...mapState("switchAccountChild", ["currentChild"]),

    path() {
      if (
        hasValidRole(this.role, [Role.Admin, Role.Principal]) ||
        isDynamicRole(this.info, this.role)
      ) {
        return service_paths.classes;
      } else if (hasValidRole(this.role, Role.Family)) {
        return `${service_paths.users}/${getId(this.currentChild)}/classes`;
      } else {
        return `${service_paths.users}/${getId(this.info)}/classes`;
      }
    }
  },
  methods: {
    reFetchTableData() {
      return this.$refs.classTableIns.getTableData();
    },

    genModalCancelAction() {
      return {
        title: "common.cancel",
        action: () => {
          this.$modal.hide(this.confirmModalProps.name);
        }
      };
    },

    confirmConclude(classObj) {
      const classId = getId(classObj);

      this.confirmModalProps = Object.assign({}, this.confirmModalProps, {
        content: this.$t("class.concluded_class_cant_be_revert"),
        primaryAction: {
          title: "common.conclude",
          action: () => {
            this.$axios
              .post(`${this.path}/${classId}/conclude`)
              .then(async () => {
                await this.reFetchTableData();
                Message.success(this.$t("class.conclude_class_successfully"));
                //sync status to cms
                await this.$axios.cms_post(`${cms_service_paths.update_class_status}`, {
                  class_id_ref: classId,
                  status: "Concluded"
                })
              })
              .finally(() => {
                this.$modal.hide(this.confirmModalProps.name);
              });
          }
        },
        secondaryActions: [{ ...this.genModalCancelAction() }]
      });

      this.$modal.show(this.confirmModalProps.name);
    },

    async confirmDelete(classObj) {
      const classId = getId(classObj);

      try {
        // set-up modal data
        const studentsRes = await this.$axios.get(
          `${this.path}/${classId}/students`
        );

        const studentQuantity = studentsRes && studentsRes?.data?.data.length;

        if (!!studentQuantity) {
          const studentTxt = studentQuantity > 1 ? "students" : "student";

          this.confirmModalProps = Object.assign({}, this.confirmModalProps, {
            content: this.$t("class.content_modal", {
              studentQuantity: studentQuantity,
              studentTxt: studentTxt
            }),
            primaryAction: {
              title: "common.ok",
              action: () => {
                this.$modal.hide(this.confirmModalProps.name);
              }
            },
            secondaryActions: []
          });
        } else {
          this.confirmModalProps = Object.assign({}, this.confirmModalProps, {
            content: this.$t("class.deleted_class_cant_be_recovered"),
            primaryAction: {
              title: "common.delete",
              action: () => {
                this.$axios
                  .delete(`${this.path}/${classId}`)
                  .then(async (isSuccess) => {
                    await this.reFetchTableData();
                    Message.success(this.$t("common.delete_success"));
                    if (isSuccess) {
                      let objParam = {
                        id_ref: classId
                      }
                      await this.$axios.cms_post(`${cms_service_paths.class_delete}`, [objParam], {}, {}, null, false)

                    }
                  })
                  .finally(() => {
                    this.$modal.hide(this.confirmModalProps.name);
                  });
              },
              class: "md-danger"
            },
            secondaryActions: [{ ...this.genModalCancelAction() }]
          });
        }

        this.$modal.show(this.confirmModalProps.name);
      } catch (error) {
        throw error;
      }
    },

    confirmCopy(classObj) {
      const classId = getId(classObj);

      this.copyClassModalProps = Object.assign({}, this.copyClassModalProps, {
        title: this.$t("class.make_a_copy_from") + classObj.name,
        content: {
          desc: this.$t("class.desc_confirm_copy_class", {
            className: classObj.name
          }),
          className: `Copy of ${classObj.name}`,
          includeStudents: false,
          includeTeachers: false
        },
        primaryAction: {
          title: "common.copy",
          action: () => {
            const {
              className,
              includeStudents,
              includeTeachers
            } = this.copyClassModalProps.content;

            this.$axios
              .post(`${this.path}/${classId}/copy`, {
                copy_students: includeStudents,
                copy_teachers: includeTeachers,
                name: className
              })
              .then(async () => {
                await this.reFetchTableData();
                Message.success(this.$t("class.copy_success"));
              })
              .finally(() => {
                this.$modal.hide(this.copyClassModalProps.name);
              });
          }
        }
      });

      this.$modal.show(this.copyClassModalProps.name);
    },

    getTermOpts() {
      return this.$axios
        .get(service_paths.terms, {
          limit: 10000
        })
        .then(response => {
          let options = [];

          response.data.data.forEach(term => {
            options.push({ id: getId(term), name: term.name });
          });

          this.formSearch[1].options = [...DEFAULT_OPTION_ALL, ...options];
        });
    },

    getTeacherOpts(queryObj) {
      return this.$axios
        .get(
          `${service_paths.staffs}/?role_name=Teacher`,
          queryObj,
          undefined,
          undefined,
          undefined,
          undefined,
          false
        )
        .then(response => {
          let options = [];

          response.data.data.forEach(teacher => {
            options.push({ id: getId(teacher), name: teacher.username });
          });

          this.formSearch[4].options = options;
        });
    },

    getLMSOptions() {
      return this.$axios.get(service_paths.lms).then(({ data }) => {
        if (data) {
          this.formSearch[3].options = [...DEFAULT_OPTION_ALL, ...data.data];
        }
      });
    },
    formatTableData(tableData) {
      return tableData.map(classData => {
        const primaryTeacher = classData.teachers.find(
          teacher => teacher.assignment === TEACHER_ASSIGNMENTS.PRIMARY
        );
        classData.teacher = primaryTeacher?.users?.user_no_sql?.full_name || "";
        classData.term = classData.terms?.name;
        classData.status_class = generateStatus(classData.status);
        classData.credit = classData.subject?.credit;
        classData.start_date = convertDateFromServer(
          classData.start_date,
          "date"
        );
        classData.end_date = convertDateFromServer(classData.end_date, "date");
        classData.source = classData.lms?.name;
        classData.external_id =
          classData.external_id &&
          `<p class="w-max inline-block">${classData.lms.name}: ${classData.external_id}</p>`;
        return classData;
      });
    }
  },

  created() {
    this.getTermOpts();
    this.getLMSOptions();
    if (
      hasValidRole(this.role, [Role.Admin, Role.Principal]) ||
      isDynamicRole(this.info, this.role)
    ) {
      this.getTeacherOpts();
    } else {
      this.formSearch = this.formSearch.filter(el => el.name !== "teacher_id");
    }
  },
  provide() {
    return {
      confirmConclude: this.confirmConclude,
      confirmDelete: this.confirmDelete,
      confirmCopy: this.confirmCopy
    };
  }
};
</script>

<style scoped lang="scss"></style>
